/**
 * seedShuffle.js
 *
 * Deterministic shuffle using a seeded pseudo-random number generator (PRNG).
 * This ensures both Player 1 and Player 2 receive the exact same question
 * order when given the same seed, which is critical for fair async play.
 *
 * Uses a simple mulberry32 PRNG — fast, deterministic, good distribution.
 */

/**
 * Mulberry32 PRNG — returns a function that produces a float in [0, 1)
 * each time it's called, deterministically from the seed.
 *
 * @param {number} seed - 32-bit integer seed
 * @returns {() => number} - stateful PRNG function
 */
export function createRNG(seed) {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Fisher-Yates shuffle using a seeded PRNG.
 * Returns a new shuffled array — does NOT mutate the original.
 *
 * @param {Array} array - input array to shuffle
 * @param {number} seed - integer seed for deterministic ordering
 * @returns {Array} - new shuffled array
 */
export function seededShuffle(array, seed) {
  const rng = createRNG(seed);
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * Generates a random integer seed.
 * Uses crypto API when available, falls back to Math.random.
 *
 * @returns {number} - random 32-bit integer seed
 */
export function generateSeed() {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const arr = new Uint32Array(1);
    crypto.getRandomValues(arr);
    return arr[0];
  }
  return Math.floor(Math.random() * 0xffffffff);
}
